// DeliveryLocation.js – Redesigned to match Dashboard UI/UX

import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Button,
  Paper,
  TextField,
  Dialog,
  DialogContent,
  DialogActions,
  DialogTitle,
  Divider,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
} from "@mui/material";
import { useParams, useNavigate } from "react-router-dom";
import CheckoutBreadcrumbs from "./CheckoutBreadcrumbs";
import MapPicker from "../components/Registration/MapPicker";
import api from "../api/axios";
import { getCheckout, setCheckout } from "../utils/buyNowStorage";
import { colors } from "../utils/theme";

export default function DeliveryLocation() {
  const navigate = useNavigate();
  const { id } = useParams();

  const [user, setUser] = useState(null);
  const [savedAddresses, setSavedAddresses] = useState([]);
  const [selectedAddressId, setSelectedAddressId] = useState(null);

  const [openAdd, setOpenAdd] = useState(false);
  const [newAddress, setNewAddress] = useState({
    addressLine1: "",
    addressLine2: "",
    city: "",
    state: "",
    pincode: "",
    addressType: "Home",
    otherType: "",
  });
  const [mapKey, setMapKey] = useState(0);
  const [addressError, setAddressError] = useState("");

  const checkout = getCheckout();

  /* ---------------- LOAD PRODUCT INTO CHECKOUT ---------------- */
  useEffect(() => {
    if (!id) return;

    const loadProduct = async () => {
      try {
        const res = await api.get(`/reels/${id}`);
        const product = res.data;

        setCheckout({
          mode: "BUY_NOW",
          items: [
            {
              id: product._id || product.id,
              title: product.title,
              price: Number(product.price) || 0,
              imageUrl: product.images?.[0]?.thumbnailKey || product.images?.[0] || product.imageUrl,
              qty: 1,
              vendorId: product.vendorId,
              vendorName: product.vendorName,
            },
          ],
        });
      } catch (err) {
        console.error("Failed to load product", err);
      }
    };

    loadProduct();
  }, [id]);

  /* ---------------- LOAD USER + ADDRESSES ---------------- */
  useEffect(() => {
    const storedUser = JSON.parse(localStorage.getItem("user"));
    if (!storedUser?.username) return;

    setUser(storedUser);

    api
      .get(`/users/${storedUser.username}/addresses`)
      .then((res) => {
        const addresses = res.data.addresses || [];
        setSavedAddresses(addresses);

        const defaultAddr = addresses.find((a) => a.isDefault);
        if (defaultAddr) setSelectedAddressId(defaultAddr._id);
      })
      .catch(() => setSavedAddresses([]));
  }, []);

  /* ---------------- CONTINUE ---------------- */
  const handleContinue = () => {
    if (!selectedAddressId) {
      alert("Please select a delivery address");
      return;
    }

    if (checkout) {
      setCheckout({ ...checkout, addressId: selectedAddressId });
    }

    navigate("/order-summary", {
      state: { addressId: selectedAddressId },
    });
  };

  /* ---------------- SAVE ADDRESS ---------------- */
  const handleSaveAddress = async () => {
    const { addressLine1, city, state, pincode, addressType, otherType } = newAddress;
    if (!addressLine1 || !city || !state || !pincode) {
      setAddressError("Please fill all required address fields");
      return;
    }
    if (addressType === "Other" && !otherType) {
      setAddressError("Please specify the address type");
      return;
    }

    setAddressError("");

    try {
      const res = await api.post(
        `/users/${user.username}/addresses`,
        newAddress
      );
      setSavedAddresses(res.data.addresses || []);
      setOpenAdd(false);
      setMapKey((k) => k + 1);
      setNewAddress({
        addressLine1: "",
        addressLine2: "",
        city: "",
        state: "",
        pincode: "",
        addressType: "Home",
        otherType: "",
      });
    } catch {
      setAddressError("Failed to save address");
    }
  };

  /* ---------------- MAP LOCATION SELECTED ---------------- */
  const handleLocationSelected = (location) => {
    setNewAddress((prev) => ({
      ...prev,
      location,
    }));
  };

  /* ---------------- MAP ADDRESS FILLED ---------------- */
  const handleAddressFilled = (fields) => {
    setNewAddress((prev) => ({
      ...prev,
      ...fields,
    }));
  };

  /* ---------------- UI ---------------- */
  return (
    <Box sx={{ maxWidth: 720, mx: "auto", px: { xs: 1.5, sm: 2 }, py: { xs: 2, sm: 3 }, minHeight: "100vh", bgcolor: colors.surfaceBright }}>
      <CheckoutBreadcrumbs active={0} />

      <Typography variant="h5" fontWeight={700} sx={{ mb: 2, color: colors.onSurface, fontSize: { xs: "1.1rem", sm: "1.5rem" } }}>
        Select a delivery address
      </Typography>

      <Paper sx={{ p: { xs: 1.5, sm: 2 }, mb: 3, borderRadius: 1, boxShadow: "0 2px 4px rgba(0,0,0,0.08)", border: "1px solid", borderColor: colors.outlineVariant }}>
        {savedAddresses.length === 0 ? (
          <Typography sx={{ color: colors.textMuted, fontSize: { xs: 14, sm: 16 } }}>
            No saved addresses found.
          </Typography>
        ) : (
          <RadioGroup
            value={selectedAddressId}
            onChange={(e) => setSelectedAddressId(e.target.value)}
          >
            {savedAddresses.map((addr) => (
              <Paper key={addr._id} sx={{ p: { xs: 1.5, sm: 2 }, mb: 2, borderRadius: 1, border: "1px solid", borderColor: selectedAddressId === addr._id ? colors.primary : colors.outlineVariant, boxShadow: selectedAddressId === addr._id ? "0 0 0 1px rgba(0,86,195,0.15)" : "none" }}>
                <FormControlLabel
                  value={addr._id}
                  control={<Radio sx={{ color: colors.outlineVariant, '&.Mui-checked': { color: colors.primary } }} />}
                  sx={{ alignItems: "flex-start", mx: 0, width: "100%" }}
                  label={
                    <Box sx={{ width: "100%" }}>
                      <Typography fontWeight={600} sx={{ color: colors.textMain, fontSize: { xs: 14, sm: 16 } }}>
                        {user?.firstName} {user?.lastName}
                      </Typography>
                      <Typography fontSize={{ xs: 13, sm: 14 }} sx={{ color: colors.onSurfaceVariant, wordBreak: "break-word" }}>
                        {addr.addressLine1}, {addr.city}, {addr.state}{" "}
                        {addr.pincode}
                      </Typography>
                    </Box>
                  }
                />
              </Paper>
            ))}
          </RadioGroup>
        )}

        <Button
          fullWidth
          variant="outlined"
          sx={{ mt: 2, borderColor: colors.outlineVariant, color: colors.onSurfaceVariant, textTransform: "none", fontWeight: 600, borderRadius: 1, py: { xs: 1.2, sm: 0.8 }, fontSize: { xs: 14, sm: 14 }, "&:hover": { borderColor: colors.primary, color: colors.primary } }}
          onClick={() => setOpenAdd(true)}
        >
          ➕ Add new address
        </Button>
      </Paper>

      <Divider sx={{ my: 2, borderColor: colors.outlineVariant }} />

      <Button fullWidth variant="contained" onClick={handleContinue} sx={{ bgcolor: colors.primary, textTransform: "none", fontWeight: 600, borderRadius: 1, py: { xs: 1.4, sm: 1.2 }, fontSize: { xs: 15, sm: 14 }, "&:hover": { bgcolor: colors.primaryContainer } }}>
        Deliver to this address
      </Button>

      {/* ---------------- ADD ADDRESS DIALOG WITH MAP ---------------- */}
      <Dialog open={openAdd} onClose={() => setOpenAdd(false)} fullWidth maxWidth="sm" PaperProps={{ sx: { borderRadius: { xs: 0, sm: 1 }, height: { xs: "100dvh", sm: "auto" }, maxHeight: { xs: "100dvh", sm: "90dvh" } } }}>
        <DialogTitle sx={{ color: colors.onSurface, fontWeight: 700, fontSize: { xs: "1rem", sm: "1.25rem" }, px: { xs: 2, sm: 3 }, pt: { xs: 2, sm: 2 } }}>Add New Delivery Address</DialogTitle>
        <DialogContent sx={{ py: 2, px: { xs: 2, sm: 3 }, overflowY: "auto", WebkitOverflowScrolling: "touch" }}>
          {addressError && (
            <Alert severity="error" sx={{ mb: 2, borderRadius: 1, fontSize: { xs: 13, sm: 14 } }}>{addressError}</Alert>
          )}

          {/* MAP PICKER */}
          <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1, color: colors.onSurface, fontSize: { xs: 13, sm: 14 } }}>
            Step 1: Set your delivery location on the map
          </Typography>
          <Box key={mapKey}>
            <MapPicker
              setValue={(field, value) => {
                if (field === "location") {
                  handleLocationSelected(value);
                } else {
                  handleAddressFilled({ [field]: value });
                }
              }}
              addressFieldName="addressLine1"
            />
          </Box>

          <Divider sx={{ my: 2, borderColor: colors.outlineVariant }} />

          {/* ADDRESS DETAILS */}
          <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1, color: colors.onSurface, fontSize: { xs: 13, sm: 14 } }}>
            Step 2: Confirm or edit your address details
          </Typography>

          <TextField
            label="Address Line 1"
            fullWidth
            sx={{ mb: 2, '& .MuiOutlinedInput-root': { borderRadius: 1 }, '& .MuiInputLabel-root': { fontSize: { xs: 14, sm: 16 } }, '& .MuiOutlinedInput-input': { fontSize: { xs: 14, sm: 16 } } }}
            value={newAddress.addressLine1}
            onChange={(e) =>
              setNewAddress({ ...newAddress, addressLine1: e.target.value })
            }
            required
          />
          <TextField
            label="Address Line 2 (optional)"
            fullWidth
            sx={{ mb: 2, '& .MuiOutlinedInput-root': { borderRadius: 1 }, '& .MuiInputLabel-root': { fontSize: { xs: 14, sm: 16 } }, '& .MuiOutlinedInput-input': { fontSize: { xs: 14, sm: 16 } } }}
            value={newAddress.addressLine2}
            onChange={(e) =>
              setNewAddress({ ...newAddress, addressLine2: e.target.value })
            }
          />
          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 2, mb: 2 }}>
            <TextField
              label="Pincode"
              value={newAddress.pincode}
              onChange={(e) =>
                setNewAddress({ ...newAddress, pincode: e.target.value.replace(/\D/g, "") })
              }
              inputProps={{ maxLength: 6 }}
              fullWidth
              required
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1 }, '& .MuiInputLabel-root': { fontSize: { xs: 14, sm: 16 } }, '& .MuiOutlinedInput-input': { fontSize: { xs: 14, sm: 16 } } }}
            />
            <TextField
              label="City"
              value={newAddress.city}
              onChange={(e) =>
                setNewAddress({ ...newAddress, city: e.target.value })
              }
              fullWidth
              required
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1 }, '& .MuiInputLabel-root': { fontSize: { xs: 14, sm: 16 } }, '& .MuiOutlinedInput-input': { fontSize: { xs: 14, sm: 16 } } }}
            />
          </Box>
          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 2, mb: 2 }}>
            <TextField
              label="State"
              value={newAddress.state}
              onChange={(e) =>
                setNewAddress({ ...newAddress, state: e.target.value })
              }
              fullWidth
              required
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1 }, '& .MuiInputLabel-root': { fontSize: { xs: 14, sm: 16 } }, '& .MuiOutlinedInput-input': { fontSize: { xs: 14, sm: 16 } } }}
            />
            <FormControl fullWidth>
              <InputLabel sx={{ fontSize: { xs: 14, sm: 16 } }}>Address Type</InputLabel>
              <Select
                value={newAddress.addressType}
                onChange={(e) =>
                  setNewAddress({ ...newAddress, addressType: e.target.value })
                }
                label="Address Type"
                sx={{ borderRadius: 1, '& .MuiSelect-select': { fontSize: { xs: 14, sm: 16 } } }}
              >
                <MenuItem value="Home">Home</MenuItem>
                <MenuItem value="Work">Work</MenuItem>
                <MenuItem value="Other">Other</MenuItem>
              </Select>
            </FormControl>
          </Box>
          {newAddress.addressType === "Other" && (
            <TextField
              label="Specify Address Type"
              fullWidth
              sx={{ mb: 2, '& .MuiOutlinedInput-root': { borderRadius: 1 }, '& .MuiInputLabel-root': { fontSize: { xs: 14, sm: 16 } }, '& .MuiOutlinedInput-input': { fontSize: { xs: 14, sm: 16 } } }}
              value={newAddress.otherType}
              onChange={(e) =>
                setNewAddress({ ...newAddress, otherType: e.target.value })
              }
            />
          )}
        </DialogContent>
        <DialogActions sx={{ px: { xs: 2, sm: 3 }, pb: { xs: 3, sm: 2 }, gap: 1 }}>
          <Button onClick={() => { setOpenAdd(false); setAddressError(""); }} sx={{ color: colors.onSurfaceVariant, textTransform: "none", fontWeight: 600, fontSize: { xs: 14, sm: 14 }, flex: { xs: 1, sm: "none" } }}>
            Cancel
          </Button>
          <Button variant="contained" onClick={handleSaveAddress} sx={{ bgcolor: colors.primary, textTransform: "none", fontWeight: 600, borderRadius: 1, fontSize: { xs: 14, sm: 14 }, py: { xs: 1, sm: 0.6 }, flex: { xs: 1, sm: "none" }, "&:hover": { bgcolor: colors.primaryContainer } }}>
            Save Address
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}